import { defineMessages } from 'react-intl'
import type { IntlShape, MessageDescriptor } from 'react-intl'
import { OFFER_TYPE_HOTEL, OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2, OFFER_TYPE_HOTEL_SLUG, OFFER_TYPE_TOUR_SLUG, OFFER_TYPE_VILLA, OFFER_TYPE_BED_BANK } from 'constants/offer'
import { urlTransform, humanTransform } from 'lib/string/removeSpaceUtils'
import config from 'constants/config'
import { DESTINATION_PRODUCT_DOT_ROUTE_PRODUCT_TYPES } from './destinationProductRouteTypes'
import { resolveLocalizedConfigValue } from 'constants/config/resolveLocalizedConfigValue'
import { nonNullable } from 'lib/array/arrayUtils'
import productLabels from 'i18n/productLabels'
import placesShortlist from 'lib/offer/destinationPlacesShortlist'

function isPlacesShortlistLocation(location: string) {
  return (placesShortlist as ReadonlyArray<string>).includes(location)
}

const destinationProductPageMessages = defineMessages({
  holidayPackages: {
    id: 'hgCkhW7b8ReQszqfVQWy',
    defaultMessage: 'Holiday Packages',
  },
  holidayHomes: {
    id: 'pGboMpShQm-2lNWez1dR',
    defaultMessage: 'Holiday Homes',
  },
})

export const escapeTypeMessages = defineMessages({
  adultsOnly: {
    id: 'CoxiDtCv3D7xOW3uJRaA',
    defaultMessage: 'Adults-only',
  },
  allInclusive: {
    id: 'NfUJOeiFtV92-f6gUkez',
    defaultMessage: 'All-inclusive',
  },
  boutique: {
    id: 'GD_t6kgQEISDtiHRs-6j',
    defaultMessage: 'Boutique',
  },
  cityBreak: {
    id: 'f-JyNXHz9iQF7xftacXK',
    defaultMessage: 'City break',
  },
  cruises: productLabels.cruises,
  familyFriendly: {
    id: 'CzhyN45CEKa1qHhWjNR7',
    defaultMessage: 'Family Friendly',
  },
  group: {
    id: 'saoQS6-xSjdZGFlfxDER',
    defaultMessage: 'Group',
  },
  homesAndVillas: productLabels.homesAndVillas,
  honeymoon: {
    id: 'E35eWbeTHfE1t0q3Z45j',
    defaultMessage: 'Honeymoon',
  },
  partyVibe: {
    id: '_sUiH3UTAvyDJ4f8A3Jk',
    defaultMessage: 'Party vibe',
  },
  relaxed: {
    id: 'JtWhs3P7ihiqo2BXjc3F',
    defaultMessage: 'Relaxed',
  },
  romantic: {
    id: '5Um8d6n765iKbkkHOt__',
    defaultMessage: 'Romantic',
  },
  schoolHoliday: {
    id: 'vMvO5OIlK3l5ZyGjiRKV',
    defaultMessage: 'School holiday',
  },
  shortStay: {
    id: 'Ak6A4-_2BhzjCOCWB5Mr',
    defaultMessage: 'Short Stay',
  },
  ski: {
    id: 'aF55NEYY5A2gVggh0E5d',
    defaultMessage: 'Ski',
  },
  spaBreak: {
    id: 'jXJarV_lmimiADNLeomh',
    defaultMessage: 'Spa break',
  },
  sunAndBeach: {
    id: 'fdvHkWA1hNV3naK7baED',
    defaultMessage: 'Sun & Beach',
  },
  tours: productLabels.tours,
  toursAndCruises: {
    id: 'W_Z4YUwUkErDu6Y6HwZY',
    defaultMessage: 'Tours and cruises',
  },
  toursByLuxuryEscapes: {
    id: 'WCye8TxAi6EQlH7rxwsx',
    defaultMessage: 'Tours by Luxury Escapes',
  },
  villa: {
    id: 'N-w_aCSIJejvu52drgi5',
    defaultMessage: 'Villa',
  },
})

/**
 * Canonical English holiday-type values used for URL decoding and API filter matching.
 * Localised labels are derived from the message descriptors above.
 */
export const ESCAPE_TYPE = {
  ADULTS_ONLY: 'Adults-only',
  ALL_INCLUSIVE: 'All-inclusive',
  BOUTIQUE: 'Boutique',
  CITY_BREAK: 'City break',
  CRUISES: 'Cruises',
  FAMILY_FRIENDLY: 'Family Friendly',
  GROUP: 'Group',
  HOMES_AND_VILLAS: 'Homes & Villas',
  HONEYMOON: 'Honeymoon',
  PARTY_VIBE: 'Party vibe',
  RELAXED: 'Relaxed',
  ROMANTIC: 'Romantic',
  SCHOOL_HOLIDAY: 'School holiday',
  SHORT_STAY: 'Short Stay',
  SKI: 'Ski',
  SPA_BREAK: 'Spa break',
  SUN_AND_BEACH: 'Sun & Beach',
  TOURS: 'Tours',
  TOURS_AND_CRUISES: 'Tours and cruises',
  TOURS_BY_LUXURY_ESCAPES: 'Tours by Luxury Escapes',
  VILLA: 'Villa',
} as const

export type EscapeType = typeof ESCAPE_TYPE[keyof typeof ESCAPE_TYPE]

export const escapeTypes: ReadonlyArray<EscapeType> = Object.values(ESCAPE_TYPE)

/**
 * Canonical English product-type values used for URL decoding and page routing.
 * Localised labels are derived from the message descriptors above.
 */
export const LANDING_PAGE_PRODUCT_TYPE = {
  HOLIDAY_PACKAGES: 'Holiday Packages',
  HOTELS: 'Hotels',
  TOURS: 'Tours',
  HOLIDAY_HOMES: 'Holiday Homes',
} as const

export type LandingPageProductType = typeof LANDING_PAGE_PRODUCT_TYPE[keyof typeof LANDING_PAGE_PRODUCT_TYPE]

export const landingPageProductNames: ReadonlyArray<LandingPageProductType> = Object.values(LANDING_PAGE_PRODUCT_TYPE)

export const PRODUCT_TYPE_HOTEL = LANDING_PAGE_PRODUCT_TYPE.HOTELS

const landingPageProductTypeMessageByValue = {
  [LANDING_PAGE_PRODUCT_TYPE.HOLIDAY_PACKAGES]: destinationProductPageMessages.holidayPackages,
  [LANDING_PAGE_PRODUCT_TYPE.HOTELS]: productLabels.hotels,
  [LANDING_PAGE_PRODUCT_TYPE.TOURS]: productLabels.tours,
  [LANDING_PAGE_PRODUCT_TYPE.HOLIDAY_HOMES]: destinationProductPageMessages.holidayHomes,
} as const satisfies Record<LandingPageProductType, MessageDescriptor>

const escapeTypeMessageByValue = {
  [ESCAPE_TYPE.ADULTS_ONLY]: escapeTypeMessages.adultsOnly,
  [ESCAPE_TYPE.ALL_INCLUSIVE]: escapeTypeMessages.allInclusive,
  [ESCAPE_TYPE.BOUTIQUE]: escapeTypeMessages.boutique,
  [ESCAPE_TYPE.CITY_BREAK]: escapeTypeMessages.cityBreak,
  [ESCAPE_TYPE.CRUISES]: escapeTypeMessages.cruises,
  [ESCAPE_TYPE.FAMILY_FRIENDLY]: escapeTypeMessages.familyFriendly,
  [ESCAPE_TYPE.GROUP]: escapeTypeMessages.group,
  [ESCAPE_TYPE.HOMES_AND_VILLAS]: escapeTypeMessages.homesAndVillas,
  [ESCAPE_TYPE.HONEYMOON]: escapeTypeMessages.honeymoon,
  [ESCAPE_TYPE.PARTY_VIBE]: escapeTypeMessages.partyVibe,
  [ESCAPE_TYPE.RELAXED]: escapeTypeMessages.relaxed,
  [ESCAPE_TYPE.ROMANTIC]: escapeTypeMessages.romantic,
  [ESCAPE_TYPE.SCHOOL_HOLIDAY]: escapeTypeMessages.schoolHoliday,
  [ESCAPE_TYPE.SHORT_STAY]: escapeTypeMessages.shortStay,
  [ESCAPE_TYPE.SKI]: escapeTypeMessages.ski,
  [ESCAPE_TYPE.SPA_BREAK]: escapeTypeMessages.spaBreak,
  [ESCAPE_TYPE.SUN_AND_BEACH]: escapeTypeMessages.sunAndBeach,
  [ESCAPE_TYPE.TOURS]: escapeTypeMessages.tours,
  [ESCAPE_TYPE.TOURS_AND_CRUISES]: escapeTypeMessages.toursAndCruises,
  [ESCAPE_TYPE.TOURS_BY_LUXURY_ESCAPES]: escapeTypeMessages.toursByLuxuryEscapes,
  [ESCAPE_TYPE.VILLA]: escapeTypeMessages.villa,
} as const satisfies Record<EscapeType, MessageDescriptor>

function formatLandingPageProductName(intl: IntlShape, productName: string): string {
  const message = landingPageProductTypeMessageByValue[productName as LandingPageProductType]
  return message ? intl.formatMessage(message) : productName
}

function formatEscapeTypeLabel(intl: IntlShape, escapeType: string): string {
  const message = escapeTypeMessageByValue[escapeType as EscapeType]
  return message ? intl.formatMessage(message) : escapeType
}

const escapeTypesWithoutSpace = escapeTypes.map<string>(urlTransform)
const landingPageProductNamesWithoutSpace = DESTINATION_PRODUCT_DOT_ROUTE_PRODUCT_TYPES

function getPageTypeSlug(productName: string) {
  let pageType = OFFER_TYPE_HOTEL_SLUG
  if (productName === LANDING_PAGE_PRODUCT_TYPE.TOURS) {
    pageType = OFFER_TYPE_TOUR_SLUG
  }
  return pageType
}

const DEFAULT_PAGE_TYPES: ReadonlyArray<App.OfferListOfferType> = [OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_VILLA, OFFER_TYPE_HOTEL, OFFER_TYPE_BED_BANK]

const PAGE_TYPES_BY_PRODUCT_TYPE: Record<string, ReadonlyArray<App.OfferListOfferType> | undefined> = {
  [LANDING_PAGE_PRODUCT_TYPE.HOLIDAY_HOMES]: [OFFER_TYPE_HOTEL, OFFER_TYPE_VILLA, OFFER_TYPE_BED_BANK],
  [LANDING_PAGE_PRODUCT_TYPE.TOURS]: [OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2],
}

function calculatePageTypes(productType: string): Array<App.OfferListOfferType> {
  // Holiday packages resolve to DEFAULT_PAGE_TYPES, same as any unrecognised product type.
  return [...(PAGE_TYPES_BY_PRODUCT_TYPE[productType] ?? DEFAULT_PAGE_TYPES)]
}

function calculateFilterTypes(productType: string) {
  let filterTypes: Array<string> = []
  if (productType === LANDING_PAGE_PRODUCT_TYPE.HOLIDAY_HOMES) {
    filterTypes = [ESCAPE_TYPE.HOMES_AND_VILLAS]
  }
  return filterTypes
}

function restoreCapitalisation(location: string | undefined, canonicalName?: string | undefined): string | undefined {
  if (canonicalName) {
    return canonicalName
  }
  if (location === undefined) {
    return undefined
  }
  if (location === 'usa') {
    return 'USA'
  }
  if (location == 'act') {
    return 'ACT'
  }
  return location.replace(/\b[a-z]/g, x => x.toUpperCase())
}

function getDestinationProductPage(
  location: string | undefined,
  holidayType: string | undefined,
  productType: string,
  canonicalName: string | undefined,
  intl: IntlShape,
) {
  const pageTypes = calculatePageTypes(productType)
  const filterTypes = calculateFilterTypes(productType)
  const loc = restoreCapitalisation(location, canonicalName)
  const productTypeLabel = formatLandingPageProductName(intl, productType)
  const holidayTypeLabel = holidayType ? formatEscapeTypeLabel(intl, holidayType) : undefined
  let title: string
  if (loc && holidayTypeLabel) {
    title = intl.formatMessage(
      {
        id: 'XdAnNJ67OS9Plph17KEA',
        defaultMessage: '{location} {holidayType} {productType}',
      },
      { location: loc, holidayType: holidayTypeLabel, productType: productTypeLabel },
    )
  } else if (loc) {
    title = intl.formatMessage(
      {
        id: 'd7FWn3Gc2pwYFI5JrAj4',
        defaultMessage: '{location} {productType}',
      },
      { location: loc, productType: productTypeLabel },
    )
  } else if (holidayTypeLabel) {
    title = intl.formatMessage(
      {
        id: 'at7S6JITgS71gQ2i3LZP',
        defaultMessage: '{holidayType} {productType}',
      },
      { holidayType: holidayTypeLabel, productType: productTypeLabel },
    )
  } else {
    title = productTypeLabel
  }
  if (productType === LANDING_PAGE_PRODUCT_TYPE.HOTELS && config.BRAND === 'luxuryescapes') {
    if (!loc) {
      title = intl.formatMessage({
        id: '00Yf6ukeAKXpDovioVPF',
        defaultMessage: 'Luxury Hotels',
      })
    }
    else {
      title = intl.formatMessage(
        {
          id: 'MKTcxxWREjy2RCw_0E9M',
          defaultMessage: 'Find luxury hotels in {location}',
        },
        { location: loc.split(',')[0] },
      )
    }
  }

  return {
    title,
    places: loc ? [loc] : [],
    holidayTypes: filterTypes,
    pageTypes,
  }
}

function getBaseSubheading(productType: string, intl: IntlShape): string {
  if (config.BRAND === 'luxuryescapes' && productType === LANDING_PAGE_PRODUCT_TYPE.HOTELS) {
    return intl.formatMessage({
      id: 'HANqwHzSb6yEM-1O49nf',
      defaultMessage: 'Escape with our luxury hotel deals',
    })
  }
  switch (productType) {
    case LANDING_PAGE_PRODUCT_TYPE.HOLIDAY_PACKAGES:
      return intl.formatMessage({
        id: '6Y-WCfZf7z4BOf9g7-rP',
        defaultMessage: 'Explore our Holiday Package deals',
      })
    case LANDING_PAGE_PRODUCT_TYPE.HOTELS:
      return intl.formatMessage({
        id: 'Ec_KnGfjdp5GQbwRJbz0',
        defaultMessage: 'Explore our Hotel deals',
      })
    case LANDING_PAGE_PRODUCT_TYPE.TOURS:
      return intl.formatMessage({
        id: 'NBSWeoFyMRnYORZUrMwL',
        defaultMessage: 'Explore our Tour deals',
      })
    case LANDING_PAGE_PRODUCT_TYPE.HOLIDAY_HOMES:
      return intl.formatMessage({
        id: '_eWbcE_8uaONSi8saurR',
        defaultMessage: 'Explore our Holiday Home deals',
      })
    default:
      return intl.formatMessage(
        {
          id: '5qqyDAWjsb8i6t-wiV_t',
          defaultMessage: 'Explore our {productType} deals',
        },
        { productType },
      )
  }
}

function getSubheadingWithLocation(baseSubheading: string, productType: string, loc: string, intl: IntlShape): string {
  if (config.BRAND === 'luxuryescapes' && productType === LANDING_PAGE_PRODUCT_TYPE.HOTELS) {
    return intl.formatMessage(
      {
        id: 'H97kLCSq91dLUcgqHwac',
        defaultMessage: 'Escape with our luxury hotel deals in {location}',
      },
      { location: loc },
    )
  }
  switch (productType) {
    case LANDING_PAGE_PRODUCT_TYPE.HOLIDAY_PACKAGES:
      return intl.formatMessage(
        {
          id: 'hqUS-EHuiA70YGRkd0jf',
          defaultMessage: 'Explore our Holiday Package deals in {location}',
        },
        { location: loc },
      )
    case LANDING_PAGE_PRODUCT_TYPE.HOTELS:
      return intl.formatMessage(
        {
          id: 'hbVn_ACRw3YzwDuxZ4lh',
          defaultMessage: 'Explore our Hotel deals in {location}',
        },
        { location: loc },
      )
    case LANDING_PAGE_PRODUCT_TYPE.TOURS:
      return intl.formatMessage(
        {
          id: 'FMp6OHqMZxBiWC8NZ3MT',
          defaultMessage: 'Explore our Tour deals in {location}',
        },
        { location: loc },
      )
    case LANDING_PAGE_PRODUCT_TYPE.HOLIDAY_HOMES:
      return intl.formatMessage(
        {
          id: 'eG7Tw2s8Pmw0l8lNQT6v',
          defaultMessage: 'Explore our Holiday Home deals in {location}',
        },
        { location: loc },
      )
    default:
      return intl.formatMessage(
        {
          id: 'aKAMS_CLSk5VF0qP1ZnJ',
          defaultMessage: '{baseSubheading} in {location}',
        },
        { baseSubheading, location: loc },
      )
  }
}

function getSubheading(
  location: string | undefined,
  productType: string,
  canonicalName: string | undefined,
  intl: IntlShape,
): string {
  const loc = restoreCapitalisation(location, canonicalName)
  const baseSubheading = getBaseSubheading(productType, intl)
  if (loc) {
    return getSubheadingWithLocation(baseSubheading, productType, loc, intl)
  }
  return baseSubheading
}

function getHolidayPackageDescription(
  loc: string | undefined,
  holidayType: string | undefined,
  intl: IntlShape,
) {
  const year = new Date().getFullYear()
  const yearRange = `${year}/${year + 1}`
  const holidayTypeLabel = holidayType ? formatEscapeTypeLabel(intl, holidayType) : undefined

  if (loc && holidayTypeLabel) {
    return intl.formatMessage(
      {
        id: '9jBPKj4nl8_vS3dTbGb-',
        defaultMessage: '{location} {holidayType} Holiday Package deals for travel in {yearRange}. Save on luxury hotel + flight bundles. Get exclusive inclusions.',
      },
      { location: loc, holidayType: holidayTypeLabel, yearRange },
    )
  }
  if (loc) {
    return intl.formatMessage(
      {
        id: 'ZCOn6mV8ZrRXRhX4UR6c',
        defaultMessage: '{location} Holiday Package deals for travel in {yearRange}. Save on luxury hotel + flight bundles. Get exclusive inclusions.',
      },
      { location: loc, yearRange },
    )
  }
  if (holidayTypeLabel) {
    return intl.formatMessage(
      {
        id: 'Ow8THwLBwXNYiWQH4c8r',
        defaultMessage: '{holidayType} Holiday Package deals for travel in {yearRange}. Save on luxury hotel + flight bundles. Get exclusive inclusions.',
      },
      { holidayType: holidayTypeLabel, yearRange },
    )
  }
  return intl.formatMessage(
    {
      id: '3pt-V6_AqJvipo7P3e4y',
      defaultMessage: 'Holiday Package deals for travel in {yearRange}. Save on luxury hotel + flight bundles. Get exclusive inclusions.',
    },
    { yearRange },
  )
}

function getDescription(
  location: string | undefined,
  holidayType: string | undefined,
  productType: string,
  canonicalName: string | undefined,
  intl: IntlShape,
) {
  const loc = restoreCapitalisation(location, canonicalName)
  const productTypeLabel = formatLandingPageProductName(intl, productType)
  const holidayTypeLabel = holidayType ? formatEscapeTypeLabel(intl, holidayType) : undefined
  if (productType === LANDING_PAGE_PRODUCT_TYPE.HOLIDAY_PACKAGES) {
    return getHolidayPackageDescription(loc, holidayType, intl)
  }
  if (productType === LANDING_PAGE_PRODUCT_TYPE.HOTELS) {
    if (loc) {
      return intl.formatMessage(
        {
          id: 'H9UOPVEDDjwliB9iuqie',
          defaultMessage: 'Book the most exclusive boutique, luxury and 5 star accommodation in {location} at the best prices.',
        },
        { location: loc },
      )
    }
    return intl.formatMessage({
      id: 'SlXHJ-0E-cr6_PwD7Jcn',
      defaultMessage: 'Book the most exclusive boutique, luxury and 5 star accommodation at the best prices.',
    })
  }
  const metaTitle = resolveLocalizedConfigValue(intl, config.head.metaTitleForDestinationProductPage)
  if (loc && holidayTypeLabel) {
    return intl.formatMessage(
      {
        id: 'gD3qbz549bDgRSt04V9C',
        defaultMessage: 'Find the latest deals & exclusive special offers for {holidayType} {productType} in {location}. {metaTitle}.',
      },
      {
        holidayType: holidayTypeLabel,
        productType: productTypeLabel,
        location: loc,
        metaTitle,
      },
    )
  }
  if (loc) {
    return intl.formatMessage(
      {
        id: 'fOVL-dig19V6zW7rLvUy',
        defaultMessage: 'Find the latest deals & exclusive special offers for {productType} in {location}. {metaTitle}.',
      },
      {
        productType: productTypeLabel,
        location: loc,
        metaTitle,
      },
    )
  }
  if (holidayTypeLabel) {
    return intl.formatMessage(
      {
        id: '-B2OS7bUVbgNUMcG0-kH',
        defaultMessage: 'Find the latest deals & exclusive special offers for {holidayType} {productType}. {metaTitle}.',
      },
      {
        holidayType: holidayTypeLabel,
        productType: productTypeLabel,
        metaTitle,
      },
    )
  }
  return intl.formatMessage(
    {
      id: 'UmP-7p5rS1y1iOLrJVTq',
      defaultMessage: 'Find the latest deals & exclusive special offers for {productType}. {metaTitle}.',
    },
    {
      productType: productTypeLabel,
      metaTitle,
    },
  )
}

function getLuxHotelMetaDescription(productType: string, location: string | undefined, canonicalName: string | undefined, intl: IntlShape): string {
  const loc = restoreCapitalisation(location, canonicalName)

  const locations = loc?.split(',')
  const smallestGeoArea = locations?.[0] || ''
  const country = locations?.[locations.length - 1] || ''

  if (smallestGeoArea || country) {
    return intl.formatMessage(
      {
        id: '6IDL_dTMPpAmDicNFW3J',
        defaultMessage: 'Exclusive deals on luxury hotels & resorts in {smallestGeoArea},{country}. Enjoy 5-star accommodation at the best prices only with {brandTitle}.',
      },
      {
        smallestGeoArea,
        country,
        brandTitle: config.title,
      },
    )
  }
  return intl.formatMessage(
    {
      id: 'LuT2gGq3wck8GdHhA8iU',
      defaultMessage: 'Exclusive deals on luxury hotels & resorts. Enjoy 5-star accommodation at the best prices only with {brandTitle}.',
    },
    {
      brandTitle: config.title,
    },
  )
}

function getMetaHeadTitle(
  regionCode: string,
  location: string | undefined,
  holidayType: string | undefined,
  productType: string,
  canonicalName: string | undefined,
  isExperiment: boolean | undefined,
  intl: IntlShape,
) {
  if (isExperiment) {
    return getExperimentHolidayPackageHeadDataTitle(productType, holidayType, location, canonicalName, intl)
  }

  const isLuxHotel = isLuxuryEscapesHotel(productType)
  if (isLuxHotel) {
    return getLuxHotelHeadDataTitle(productType, location, canonicalName, intl)
  }
  else {
    const headTitle = getHeadTitle(location, holidayType, productType, canonicalName, intl)
    return intl.formatMessage(
      {
        id: 'LDslusDCanqkuVFUUv0Q',
        defaultMessage: '{headTitle} - {brandTitle} {regionCode}',
      },
      {
        headTitle,
        brandTitle: config.title,
        regionCode,
      },
    )
  }
}

function getMetaDescription(
  location: string | undefined,
  holidayType: string | undefined,
  productType: string,
  canonicalName: string | undefined,
  isExperiment: boolean | undefined,
  intl: IntlShape,
) {
  if (isExperiment) {
    return getExperimentHolidayPackageMetaDescription(productType, location, canonicalName, intl)
  }

  const isLuxHotel = isLuxuryEscapesHotel(productType)
  if (isLuxHotel) {
    return getLuxHotelMetaDescription(productType, location, canonicalName, intl)
  }
  return getDescription(location, holidayType, productType, canonicalName, intl)
}

function getHolidayPackageHeadTitle(loc: string | undefined, holidayType: string | undefined, intl: IntlShape): string {
  const year = new Date().getFullYear()
  const yearRange = `${year}/${year + 1}`
  const holidayTypeLabel = holidayType ? formatEscapeTypeLabel(intl, holidayType) : undefined

  if (loc && holidayTypeLabel) {
    return intl.formatMessage(
      {
        id: 'DT12Tf9Xt1H1pksCzAo_',
        defaultMessage: '{location} {holidayType} Holidays & Package Deals {yearRange}',
      },
      { location: loc, holidayType: holidayTypeLabel, yearRange },
    )
  }
  if (loc) {
    return intl.formatMessage(
      {
        id: '9lP6XSwElFn3mMgOL_b8',
        defaultMessage: '{location} Holidays & Package Deals {yearRange}',
      },
      { location: loc, yearRange },
    )
  }
  if (holidayTypeLabel) {
    return intl.formatMessage(
      {
        id: 'idUHhNXBgN9vFwaTc5mW',
        defaultMessage: '{holidayType} Holidays & Package Deals {yearRange}',
      },
      { holidayType: holidayTypeLabel, yearRange },
    )
  }
  return intl.formatMessage(
    {
      id: 'L2-IfRNU8SrGalNZzick',
      defaultMessage: 'Holidays & Package Deals {yearRange}',
    },
    { yearRange },
  )
}

function getHotelHeadTitle(loc: string | undefined, holidayType: string | undefined, intl: IntlShape): string {
  const holidayTypeLabel = holidayType ? formatEscapeTypeLabel(intl, holidayType) : undefined

  if (config.BRAND === 'luxuryescapes') {
    if (loc) {
      return intl.formatMessage(
        {
          id: 'x65-boPxY4gYC5q83iYn',
          defaultMessage: 'Best Luxury and 5 Star Hotels and Resorts in {location}',
        },
        { location: loc },
      )
    }
    return intl.formatMessage({
      id: 'LaUPJMNDP-THVdb92CcN',
      defaultMessage: 'Best Luxury and 5 Star Hotels and Resorts',
    })
  }
  if (loc && holidayTypeLabel) {
    return intl.formatMessage(
      {
        id: 'IC1Tz3CF4ANoPwjPtKMx',
        defaultMessage: '4 & 5 Star {holidayType} Hotels in {location}',
      },
      { holidayType: holidayTypeLabel, location: loc },
    )
  }
  if (loc) {
    return intl.formatMessage(
      {
        id: 'NyyfkvPACJ1hrqPIsc3R',
        defaultMessage: '4 & 5 Star Hotels in {location}',
      },
      { location: loc },
    )
  }
  if (holidayTypeLabel) {
    return intl.formatMessage(
      {
        id: '1OW0Qlsajk3MGot_VbBQ',
        defaultMessage: '4 & 5 Star {holidayType} Hotels',
      },
      { holidayType: holidayTypeLabel },
    )
  }
  return intl.formatMessage({
    id: 'Sp7CE6LWelsHBfSbBcvx',
    defaultMessage: '4 & 5 Star Hotels',
  })
}

function getHeadTitle(
  location: string | undefined,
  holidayType: string | undefined,
  productType: string,
  canonicalName: string | undefined,
  intl: IntlShape,
) {
  const loc = restoreCapitalisation(location, canonicalName)
  if (productType === LANDING_PAGE_PRODUCT_TYPE.HOLIDAY_PACKAGES) {
    return getHolidayPackageHeadTitle(loc, holidayType, intl)
  }
  if (productType === LANDING_PAGE_PRODUCT_TYPE.HOTELS) {
    return getHotelHeadTitle(loc, holidayType, intl)
  }
  return getTitle(location, holidayType, productType, canonicalName, intl)
}

function getSmallestGeoLocation(location: string | undefined, canonicalName?: string) {
  const loc = restoreCapitalisation(location, canonicalName)
  const smallestGeoArea = loc?.split(',')[0] || ''
  return smallestGeoArea
}

function getLuxHotelHeadDataTitle(productType: string, location: string | undefined, canonicalName: string | undefined, intl: IntlShape): string {
  const smallestGeoArea = getSmallestGeoLocation(location, canonicalName)
  if (smallestGeoArea) {
    return intl.formatMessage(
      {
        id: 'Gtkrqv96hzdmSNUQ2592',
        defaultMessage: '{location} Luxury and 5 Star Hotels and Resorts - {brandTitle}',
      },
      {
        location: smallestGeoArea,
        brandTitle: config.title,
      },
    )
  }
  return intl.formatMessage(
    {
      id: 'GqD_Qt6oVPZOGJMyb3Hr',
      defaultMessage: 'Luxury and 5 Star Hotels and Resorts - {brandTitle}',
    },
    {
      brandTitle: config.title,
    },
  )
}

function getExperimentHolidayPackageHeadDataTitle(
  productType: string,
  holidayType: string | undefined,
  location: string | undefined,
  canonicalName: string | undefined,
  intl: IntlShape,
): string {
  const smallestGeoArea = getSmallestGeoLocation(location, canonicalName)
  const year = new Date().getFullYear()
  const yearRange = `${year}/${year + 1}`
  const holidayTypeLabel = holidayType ? formatEscapeTypeLabel(intl, holidayType) : undefined

  if (smallestGeoArea && holidayTypeLabel) {
    return intl.formatMessage(
      {
        id: 'hIoHkNDFbzEGsbqBrRq3',
        defaultMessage: '{location} {holidayType} Holiday Packages & Deals {yearRange} - {brandTitle}',
      },
      {
        location: smallestGeoArea,
        holidayType: holidayTypeLabel,
        yearRange,
        brandTitle: config.title,
      },
    )
  }
  if (smallestGeoArea) {
    return intl.formatMessage(
      {
        id: 'AfAvQLr3P1tMrh5D0d56',
        defaultMessage: '{location} Holiday Packages & Deals {yearRange} - {brandTitle}',
      },
      {
        location: smallestGeoArea,
        yearRange,
        brandTitle: config.title,
      },
    )
  }
  if (holidayTypeLabel) {
    return intl.formatMessage(
      {
        id: 'ah9b5SRDB2scj1F9FFS5',
        defaultMessage: '{holidayType} Holiday Packages & Deals {yearRange} - {brandTitle}',
      },
      {
        holidayType: holidayTypeLabel,
        yearRange,
        brandTitle: config.title,
      },
    )
  }
  return intl.formatMessage(
    {
      id: 'w7FSjevaBcMlemjE2gp2',
      defaultMessage: 'Holiday Packages & Deals {yearRange} - {brandTitle}',
    },
    {
      yearRange,
      brandTitle: config.title,
    },
  )
}

function getExperimentHolidayPackageMetaDescription(productType: string, location: string | undefined, canonicalName: string | undefined, intl: IntlShape): string {
  const loc = restoreCapitalisation(location, canonicalName)

  const locations = loc?.split(',')
  const smallestGeoArea = locations?.[0] || ''
  const country = locations?.[locations.length - 1] || ''

  if (smallestGeoArea || country) {
    return intl.formatMessage(
      {
        id: 'ydHmzqvt7BfoUcdbWtGJ',
        defaultMessage: 'Discover the best {smallestGeoArea},{country} holiday package deals. Save on luxury hotel + flight packages, with exclusive inclusions only at {brandTitle}.',
      },
      {
        smallestGeoArea,
        country,
        brandTitle: config.title,
      },
    )
  }
  return intl.formatMessage(
    {
      id: 'Psa0qYpt6HM9VIxWxZ3H',
      defaultMessage: 'Discover the best holiday package deals on exclusive luxury hotels & resorts. Save on luxury hotel + flight packages, with exclusive inclusions only at {brandTitle}.',
    },
    {
      brandTitle: config.title,
    },
  )
}

function getTitle(
  location: string | undefined,
  holidayType: string | undefined,
  productType: string,
  canonicalName: string | undefined,
  intl: IntlShape,
) {
  return getDestinationProductPage(location, holidayType, productType, canonicalName, intl)?.title
}

function getRelatedTravelItems(
  location: string | undefined,
  holidayType: string | undefined,
  productType: string,
  canonicalName: string | undefined,
  intl: IntlShape,
): Array<{ text: string, url: { to: string } }> {
  const loc = restoreCapitalisation(location, canonicalName)
  const holidayTypeLabel = holidayType ? formatEscapeTypeLabel(intl, holidayType) : undefined
  return nonNullable(landingPageProductNames.map(productName => {
    const productNameLabel = formatLandingPageProductName(intl, productName)
    if (!holidayType && productName === productType) {
      return null
    }
    if (productName !== LANDING_PAGE_PRODUCT_TYPE.HOTELS && location && !isPlacesShortlistLocation(location)) {
      return null
    }
    let text: string
    if (loc && holidayTypeLabel) {
      text = intl.formatMessage(
        {
          id: '1-4TSF_j5Vyfpb1AhXjC',
          defaultMessage: '{location} {holidayType} {productName}',
        },
        { location: loc, holidayType: holidayTypeLabel, productName: productNameLabel },
      )
    } else if (loc) {
      text = intl.formatMessage(
        {
          id: '0yNYnSTq-XKlGepDxBBz',
          defaultMessage: '{location} {productName}',
        },
        { location: loc, productName: productNameLabel },
      )
    } else if (holidayTypeLabel) {
      text = intl.formatMessage(
        {
          id: 'kEGJxd4Jxt9ikdMx9DB_',
          defaultMessage: '{holidayType} {productName}',
        },
        { holidayType: holidayTypeLabel, productName: productNameLabel },
      )
    } else {
      text = productNameLabel
    }
    return {
      text,
      url: {
        to: `/${loc ? urlTransform(loc).toLowerCase() + '.' : ''}${holidayType ? urlTransform(holidayType).toLowerCase() + '.' : ''}${urlTransform(productName).toLowerCase()}`,
      },
    }
  }))
}

function isNotLowerCase(s: string): boolean {
  return s.toLowerCase() !== s
}

/**
 * True when the URL location slug differs from the place's canonical name
 * (e.g. "bali" vs "Bali, Indonesia"), so the page should consolidate SEO
 * signals onto the preferred destination product URL.
 */
function shouldCanonicaliseDestinationLocation(
  location: string | undefined,
  canonicalName: string | undefined,
): boolean {
  return Boolean(
    location &&
    canonicalName &&
    canonicalName.toLowerCase() !== location.toLowerCase(),
  )
}

/**
 * Builds a dotted destination-product path:
 * `/{region?}/{location}.{holidayType?}.{productType}`
 * Region is omitted when building a HeadData `canonical` path fragment.
 */
function buildDestinationProductDotPath({
  regionCode,
  location,
  holidayType,
  productType,
}: {
  regionCode?: string
  location: string
  holidayType?: string
  productType: string
}): string {
  const segments = [
    urlTransform(location.toLowerCase()),
    holidayType ? urlTransform(holidayType.toLowerCase()) : undefined,
    urlTransform(productType.toLowerCase()),
  ].filter((segment): segment is string => Boolean(segment))

  const path = `/${segments.join('.')}`
  return regionCode ? `/${regionCode.toLowerCase()}${path}` : path
}

// For legacy reasons, this function returns a lower case location, and an upper cased holiday type and product type.
// If a redirect due to incorrect case is needed, shouldRedirect is set to true.
// Elsewhere in this file, capitalisation of the location is achieved using the restoreCapitalisation function.
function decodeUrlParams(params: { locationOrHolidayType?: string, location?: string, holidayType?: string, productType: string, city?: string, state?: string, country?: string }) {
  const { locationOrHolidayType, location, holidayType, productType, city, state, country } = params

  let holidayType_: string | undefined = undefined
  if (holidayType || locationOrHolidayType) {
    holidayType_ = escapeTypes[escapeTypesWithoutSpace.findIndex((e) => e.toLowerCase() === (holidayType ?? locationOrHolidayType ?? '').toLowerCase())]
  }

  let location_: string | undefined = humanTransform([location, city, state, country].filter(Boolean).join(','))

  location_ = location_ !== '' ? location_ : undefined

  if (!location_ && !!locationOrHolidayType && !holidayType_) {
    location_ = humanTransform(locationOrHolidayType)
  }

  const productType_: string | undefined = landingPageProductNames[landingPageProductNamesWithoutSpace.findIndex((e) => e.toLowerCase() === productType.toLowerCase())]

  const shouldRedirect =
    isNotLowerCase(productType) ||
    (!!location_ && isNotLowerCase(location_)) ||
    (!!holidayType_ && isNotLowerCase(holidayType ?? locationOrHolidayType ?? ''))

  if (productType_ !== LANDING_PAGE_PRODUCT_TYPE.HOTELS && !shouldRedirect) {
    const locationInnerLevel = location_?.split(', ')[0] ?? ''
    location_ = isPlacesShortlistLocation(locationInnerLevel) ? location_ : undefined
  }

  return { location: location_, holidayType: holidayType_, productType: productType_, shouldRedirect }
}

// roll out SEO testing results into all the luxury Escapes  destination hotel pages
// Following logic will be used to determine if the page is related to the lux hotel
function isLuxuryEscapesHotel(productType: string): boolean {
  if (productType === LANDING_PAGE_PRODUCT_TYPE.HOTELS) {
    if (config.BRAND === 'luxuryescapes') {
      return true
    }
  }
  return false
}

// SEO experiment for .holidaypackages pages
// following logic will be used to determine if the holidaypackages experiment is on
function isExperimentOn(url: string, productType: string): boolean {
  if (productType === LANDING_PAGE_PRODUCT_TYPE.HOLIDAY_PACKAGES) {
    if (config.BRAND === 'luxuryescapes') {
      return url.length % 2 === 1
    }
  }
  return false
}

export {
  getDestinationProductPage,
  getDescription,
  getHeadTitle,
  getTitle,
  getRelatedTravelItems,
  getPageTypeSlug,
  decodeUrlParams,
  calculatePageTypes,
  getSubheading,
  restoreCapitalisation,
  getLuxHotelHeadDataTitle,
  getLuxHotelMetaDescription,
  isLuxuryEscapesHotel,
  getMetaHeadTitle,
  getMetaDescription,
  isExperimentOn,
  formatEscapeTypeLabel,
  shouldCanonicaliseDestinationLocation,
  buildDestinationProductDotPath,
}
